<template>
  <CurrentEventDataProvider v-slot="{ data, logoUrl }">
    <div class="pa-page">
      <!-- ── Top bar ──────────────────────────────────────────────── -->
      <header class="pa-topbar">
        <div class="pa-topbar__inner">
          <div class="pa-topbar__brand">
            <img
              v-if="logoUrl"
              :src="logoUrl"
              :alt="
                $t('Views.ParticipantsAdmitted.eventLogoAlt', {
                  name: data.eventName
                })
              "
              class="pa-topbar__logo"
            />
            <div class="pa-topbar__titles">
              <span class="pa-topbar__event">{{ data.eventName }}</span>
              <span class="pa-topbar__sub">{{
                $t('Views.ParticipantsAdmitted.participantAdmissions')
              }}</span>
            </div>
          </div>
        </div>
      </header>

      <!-- ── Body ─────────────────────────────────────────────────── -->
      <div class="pa-body">
        <!-- Sidebar / filters -->
        <aside
          v-if="showSlider"
          class="pa-sidebar"
          :class="{ 'pa-sidebar--open': showSlider }"
        >
          <div class="pa-sidebar__header">
            <h2 class="pa-sidebar__title">
              {{ $t('Views.PollResults.filters') }}
            </h2>
            <div class="pa-sidebar__actions">
              <button
                type="button"
                class="pa-btn pa-btn--ghost"
                :disabled="isLoading"
                @click="refreshItems"
                :aria-label="
                  isLoading
                    ? $t('Views.PollResults.loading')
                    : $t('Views.PollResults.refresh')
                "
              >
                <i
                  :class="
                    isLoading
                      ? 'fas fa-circle-notch fa-spin'
                      : 'fas fa-redo-alt'
                  "
                  aria-hidden="true"
                ></i>
                <span class="pa-btn__label">{{
                  isLoading ? '' : $t('Views.PollResults.refresh')
                }}</span>
              </button>
              <button
                type="button"
                class="pa-btn pa-btn--ghost pa-sidebar__close"
                @click="toggleSlider"
                :aria-label="$t('Views.ParticipantsAdmitted.closeFilters')"
              >
                <i class="fas fa-times" aria-hidden="true"></i>
              </button>
            </div>
          </div>

          <div class="pa-sidebar__scroll">
            <!-- Date picker — inlined here instead of using the
                 shared <Filters showDates /> control, because that
                 component's <select> picks up @tailwindcss/forms'
                 aggressive base styles that ignored every scoped
                 override. Inlining gives us full control. -->
            <section class="pa-filter-group">
              <h3 class="pa-filter-group__title">
                {{ $t('Views.PollResults.date') }}
              </h3>
              <div class="pa-filter-group__body">
                <div class="pa-date-select">
                  <select
                    v-model="dateOption"
                    @change="handleDateOptionChange"
                    class="pa-date-select__field"
                    :aria-label="
                      $t('Views.ParticipantsAdmitted.selectDateFilter')
                    "
                  >
                    <option value="default">
                      {{ $t('Views.ParticipantsAdmitted.select') }}
                    </option>
                    <option value="today">
                      {{ $t('Views.ParticipantsAdmitted.today') }}
                    </option>
                    <option value="dateRange">
                      {{ $t('Views.ParticipantsAdmitted.customDate') }}
                    </option>
                  </select>
                  <i
                    class="fas fa-chevron-down pa-date-select__chevron"
                    aria-hidden="true"
                  ></i>
                </div>

                <div v-if="dateOption === 'dateRange'" class="pa-date-range">
                  <span class="pa-date-range__label">{{
                    $t('Views.ParticipantsAdmitted.chooseDate')
                  }}</span>
                  <div class="pa-date-range__inputs">
                    <input
                      v-model="fromDate"
                      type="date"
                      class="pa-date-range__input"
                      :max="toDate || null"
                      @change="emitDateRange()"
                      :aria-label="$t('Views.ParticipantsAdmitted.fromDate')"
                    />
                    <input
                      v-model="toDate"
                      type="date"
                      class="pa-date-range__input"
                      :min="fromDate || null"
                      :disabled="!fromDate"
                      @change="emitDateRange()"
                      :aria-label="$t('Views.ParticipantsAdmitted.toDate')"
                    />
                  </div>
                </div>
              </div>
            </section>

            <!-- Status -->
            <section class="pa-filter-group">
              <h3 class="pa-filter-group__title">
                {{ $t('Views.ParticipantsAdmitted.status') }}
              </h3>
              <div class="pa-filter-group__body">
                <label class="pa-checkbox-row">
                  <span class="pa-checkbox-row__label">
                    <input
                      type="checkbox"
                      v-model="showAdmittedRows"
                      class="pa-checkbox-row__input"
                    />
                    <span>{{
                      $t('Views.ParticipantsAdmitted.scannedIn')
                    }}</span>
                  </span>
                  <span class="pa-checkbox-row__count">{{
                    admittedRowsCount
                  }}</span>
                </label>
                <label class="pa-checkbox-row">
                  <span class="pa-checkbox-row__label">
                    <input
                      type="checkbox"
                      v-model="showNoAdmittedRows"
                      class="pa-checkbox-row__input"
                    />
                    <span>{{
                      $t('Views.ParticipantsAdmitted.notScannedIn')
                    }}</span>
                  </span>
                  <span class="pa-checkbox-row__count">{{
                    noAdmittedRowsCount
                  }}</span>
                </label>
              </div>
            </section>

            <!-- Groups -->
            <section class="pa-filter-group">
              <h3 class="pa-filter-group__title">
                {{ $t('Components.Filters.groups') }}
              </h3>
              <div class="pa-filter-group__body">
                <Filters
                  showCheckbox
                  @checkbox-filter="handleCheckBoxFilters"
                  :groups="groups"
                  :tableCheckBoxName="$t('Components.Filters.groups')"
                  showGroupModal
                />
              </div>
            </section>
          </div>
        </aside>

        <!-- Main content -->
        <main class="pa-main">
          <!-- Toolbar -->
          <div class="pa-toolbar">
            <div class="pa-toolbar__search">
              <i
                class="fas fa-search pa-toolbar__search-icon"
                aria-hidden="true"
              ></i>
              <input
                v-model="searchText"
                type="text"
                :placeholder="$t('Views.Stage.Participants.participants')"
                class="pa-toolbar__search-input"
                ref="childFocus"
                :aria-label="
                  $t('Views.Stage.Participants.searchForParticipants')
                "
              />
            </div>

            <button
              v-if="!showSlider"
              type="button"
              class="pa-btn pa-btn--soft"
              @click="toggleSlider"
              :aria-label="$t('Views.ParticipantsAdmitted.openFilters')"
              :title="$t('Views.ParticipantsAdmitted.openFilters')"
            >
              <i class="fas fa-filter" aria-hidden="true"></i>
              <span class="pa-btn__label">{{
                $t('Views.PollResults.filters')
              }}</span>
            </button>

            <div class="pa-toolbar__actions">
              <button
                type="button"
                class="pa-btn pa-btn--outline"
                :disabled="loadingExport"
                @click="handleExport"
              >
                <i
                  :class="
                    loadingExport
                      ? 'fas fa-circle-notch fa-spin'
                      : 'fas fa-file-export'
                  "
                  aria-hidden="true"
                ></i>
                <span class="pa-btn__label">{{
                  loadingExport
                    ? $t('Views.ParticipantsAdmitted.exporting')
                    : $t('Views.PollResults.export')
                }}</span>
              </button>
            </div>
          </div>

          <!-- Header strip -->
          <div class="pa-list-header">
            <div class="pa-list-header__titles">
              <h1 class="pa-list-header__title">
                {{ $t('Views.ParticipantLink.participantList') }}
              </h1>
              <p class="pa-list-header__sub">
                {{
                  $t('Views.ParticipantsAdmitted.showingCount', {
                    shown: totalRowCount,
                    total: totalRowCount
                  })
                }}
              </p>
            </div>
          </div>

          <!-- Loader -->
          <div v-if="isLoading" class="pa-loading">
            <i class="fas fa-circle-notch fa-spin"></i>
          </div>

          <!-- Table -->
          <div v-else class="pa-table-card">
            <div class="pa-table-scroll">
              <table class="pa-table">
                <thead class="pa-table__head">
                  <tr>
                    <th
                      v-for="(headerColumn, i) in tableHeader"
                      :key="i"
                      class="pa-table__th"
                    >
                      {{ headerColumn }}
                    </th>
                  </tr>
                </thead>

                <tbody v-if="tableRows.length">
                  <tr
                    v-for="(row, i) in tableRows"
                    :key="i"
                    class="pa-table__tr"
                  >
                    <td
                      v-for="(hd, j) in tableHeader"
                      :key="j"
                      class="pa-table__td"
                    >
                      <span v-if="isScannedColumn(j)">
                        <span
                          v-if="scannedValue(row[j])"
                          class="pa-pill pa-pill--scanned"
                        >
                          <i class="fas fa-check-circle" aria-hidden="true"></i>
                          {{ scannedValue(row[j]) }}
                        </span>
                        <span v-else class="pa-pill pa-pill--pending">
                          <i class="far fa-clock" aria-hidden="true"></i>
                          {{ $t('Views.ParticipantsAdmitted.pending') }}
                        </span>
                      </span>
                      <span v-else>{{
                        row.hasOwnProperty(j) ? formatCellValue(row[j]) : ''
                      }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-if="!tableRows.length && !isLoading" class="pa-empty">
              <i class="far fa-user pa-empty__icon" aria-hidden="true"></i>
              <p class="pa-empty__text">
                {{ $t('Views.ParticipantsAdmitted.notFound') }}
              </p>
            </div>
          </div>
        </main>
      </div>
    </div>
  </CurrentEventDataProvider>
</template>

<script>
import ApiClient from '@/client';
import Filters from '@/components/shared/Filters.vue';
import { mapActions, mapGetters } from 'vuex';

export default {
  components: {
    Filters
  },
  data() {
    return {
      isLoading: false,
      eventId: window.location.host.split('.')[0],
      checkBoxFilter: [],
      admittedHeader: [],
      noAdmittedHeader: [],
      admittedRows: [],
      noAdmittedRows: [],
      groupsFilter: [],
      fromDate: '',
      toDate: '',
      loadingFilter: false,
      defaultHeader: ['name', 'lastName', 'company', 'email', 'phone'],
      showAdmittedRows: true,
      showNoAdmittedRows: false,
      debouncedSearchTimeout: null,
      searchText: '',
      showSlider: false,
      loadingExport: false,
      dateOption: 'default'
    };
  },
  created() {
    this.fetchGroups();
    this.loadParticipants();

    this.checkScreenSize();
    window.addEventListener('resize', this.checkScreenSize);
  },
  computed: {
    ...mapGetters('Groups', { groups: 'getItems' }),
    ...mapGetters('CurrentEvent', [{ eventName: 'getTitle' }]),
    ...mapGetters('CurrentEvent', [
      'getLocale',
      'getEvent',
      'isWebappEnabled',
      'isLobbyEnabled',
      'features'
    ]),
    filteredAdmittedRows() {
      return this.admittedRows.filter((row) =>
        this.doesRowMatchSearch(row, this.searchText)
      );
    },
    filteredNoAdmittedRows() {
      return this.noAdmittedRows.filter((row) =>
        this.doesRowMatchSearch(row, this.searchText)
      );
    },
    admittedRowsCount() {
      return this.admittedRows.length;
    },
    noAdmittedRowsCount() {
      return this.noAdmittedRows.length;
    },
    totalRowCount() {
      return (
        (this.showAdmittedRows ? this.admittedRowsCount : 0) +
        (this.showNoAdmittedRows ? this.noAdmittedRowsCount : 0)
      );
    },
    // Render both admitted and not-admitted rows under the same table
    // header (whichever set the user has currently toggled on).
    tableHeader() {
      if (
        this.showAdmittedRows &&
        this.admittedHeader &&
        this.admittedHeader.length
      ) {
        return this.admittedHeader;
      }
      return this.noAdmittedHeader || [];
    },
    tableRows() {
      const rows = [];
      if (this.showAdmittedRows) rows.push(...this.filteredAdmittedRows);
      if (this.showNoAdmittedRows) rows.push(...this.filteredNoAdmittedRows);
      return rows;
    }
  },
  methods: {
    ...mapActions('Groups', { fetchGroups: 'fetchItems' }),
    fetchItems() {
      this.loadParticipants();
    },
    toggleSlider() {
      this.showSlider = !this.showSlider;
    },
    handleDateOptionChange() {
      if (this.dateOption === 'today') {
        const today = new Date().toISOString().slice(0, 10);
        this.fromDate = today;
        this.toDate = today;
      } else if (this.dateOption === 'default') {
        this.fromDate = '';
        this.toDate = '';
      }
      this.emitDateRange();
    },
    emitDateRange() {
      this.handleDates({ from: this.fromDate, to: this.toDate });
    },
    handleDates(e) {
      this.fromDate = e.from;
      this.toDate = e.to;

      if (
        this.toDate ||
        (this.fromDate && this.toDate) ||
        (!this.fromDate && !this.toDate)
      ) {
        this.fetchItems({
          eventId: this.eventId,
          fromDate: this.fromDate,
          toDate: this.toDate,
          groups: this.groupsFilter
        });
      }
    },
    handleResetFilter() {
      this.checkBoxFilter = [];
      this.$emit('checkbox-filter', this.checkBoxFilter);
    },
    handleSaveFilter() {
      this.$emit('checkbox-filter', this.checkBoxFilter);
    },
    handleCheckBox(e) {
      const value = e.target.value.trim();
      const isChecked = this.checkBoxFilter.find((v) => v === value);
      if (!isChecked) {
        this.checkBoxFilter.push(value);
      } else {
        this.checkBoxFilter = this.checkBoxFilter.filter(
          (v) => v.trim() !== value
        );
      }
    },
    handleCheckBoxFilters(e) {
      this.groupsFilter = e;
      this.loadingFilter = true;
      this.fetchItems({
        eventId: this.eventId,
        fromDate: this.fromDate,
        toDate: this.toDate,
        groups: this.groupsFilter
      });
    },
    filterRows(rows, searchText) {
      return rows.filter((row) => {
        return Object.values(row).some((value) => {
          return value && value.toString().toLowerCase().includes(searchText);
        });
      });
    },
    filterData() {
      const searchText = this.searchText.toLowerCase();
      this.admittedRows = this.filterRows(this.admittedRows, searchText);
      this.noAdmittedRows = this.filterRows(this.noAdmittedRows, searchText);
    },
    formatCellValue(value) {
      if (Array.isArray(value)) {
        return value.join(', ');
      }
      return value ?? '';
    },

    loadParticipants() {
      this.isLoading = true;
      ApiClient.getParticipantsAdmission(
        this.groupsFilter,
        this.fromDate,
        this.toDate
      )
        .then((response) => {
          if (response.status === 200) {
            this.isLoading = false;
            this.admittedHeader = response?.data?.admittedParticipants?.header;
            this.noAdmittedHeader =
              response?.data?.noAdmittedParticipants?.header;
            this.admittedRows = response?.data?.admittedParticipants?.values;
            this.noAdmittedRows =
              response?.data?.noAdmittedParticipants?.values;
            this.filterData();
          }
        })
        .catch((e) => console.error(`Critical failure: ${e.message}`));
    },
    doesRowMatchSearch(row, searchText) {
      return Object.values(row).some((value) => {
        return (
          value &&
          value.toString().toLowerCase().includes(searchText.toLowerCase())
        );
      });
    },
    checkScreenSize() {
      if (window.innerWidth >= 1024) {
        this.showSlider = true;
      } else {
        this.showSlider = false;
      }
    },
    async handleExport() {
      this.loadingExport = true;
      await ApiClient.exportParticipantsAdmission(
        this.groupsFilter,
        this.fromDate,
        this.toDate,
        this.showAdmittedRows,
        this.showNoAdmittedRows
      );
      this.loadingExport = false;
    },
    refreshItems() {
      this.fetchItems();
    },
    // Detect the "scanned in" column by header label so we can render
    // it as a pill instead of raw JSON / timestamps.
    isScannedColumn(idx) {
      const header = this.tableHeader[idx];
      if (!header || typeof header !== 'string') return false;
      const h = header.toLowerCase();
      return h.includes('scan') || h.includes('admitted');
    },
    // Pull the most-recent timestamp out of whatever shape the API
    // returns for the "scanned in" cell — `["30.04.2026 11:04:00"]`,
    // a plain string, or `-` / null when not scanned.
    scannedValue(value) {
      if (!value && value !== 0) return '';
      if (value === '-' || value === '—') return '';
      if (Array.isArray(value)) {
        const last = value[value.length - 1];
        return last ? String(last).replace(/^"|"$/g, '') : '';
      }
      const str = String(value).trim();
      if (!str || str === '-' || str === '[]') return '';
      // Strip surrounding brackets/quotes the API sometimes serializes.
      return str.replace(/^\[\s*"?|"?\s*\]$/g, '');
    }
  },
  destroyed() {
    window.removeEventListener('resize', this.checkScreenSize);
  }
};
</script>

<style scoped lang="scss">
$primary: #047873;
$primary-dark: #03625e;
$navy: #001838;
$text: #111827;
$muted: #6b7280;
$border: #e5e7eb;
$border-soft: #f1f2f4;
$surface: #ffffff;
$surface-alt: #f9fafb;
$bg: #f3f4f6;

.pa-page {
  min-height: 100vh;
  background: $bg;
  font-family: 'Poppins', sans-serif;
  color: $text;
  display: flex;
  flex-direction: column;
}

// ── Top bar ─────────────────────────────────────────────────────
.pa-topbar {
  background: $navy;
  color: #ffffff;
  position: sticky;
  top: 0;
  z-index: 30;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);

  &__inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 14px 24px;
    min-height: 64px;
  }

  &__brand {
    display: flex;
    align-items: center;
    gap: 14px;
    min-width: 0;
  }

  &__logo {
    width: 44px;
    height: 44px;
    object-fit: contain;
    background: #ffffff;
    border-radius: 12px;
    padding: 4px;
    flex-shrink: 0;
  }

  &__titles {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  &__event {
    font-size: 16px;
    font-weight: 700;
    line-height: 1.2;
    color: #ffffff;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__sub {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.72);
    margin-top: 2px;
    letter-spacing: 0.02em;
  }

  @media (max-width: 640px) {
    &__inner {
      padding: 12px 14px;
    }
    &__event {
      font-size: 14px;
    }
    &__sub {
      font-size: 11px;
    }
  }
}

// ── Body grid ───────────────────────────────────────────────────
.pa-body {
  display: flex;
  flex: 1;
  gap: 20px;
  padding: 20px 24px 28px;
  align-items: flex-start;

  @media (max-width: 640px) {
    padding: 16px 14px 22px;
    gap: 14px;
  }
}

// ── Sidebar ─────────────────────────────────────────────────────
.pa-sidebar {
  flex-shrink: 0;
  width: 280px;
  background: $surface;
  border-radius: 14px;
  border: 1px solid $border;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  max-height: calc(100vh - 110px);
  position: sticky;
  top: 84px;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 18px 12px;
    border-bottom: 1px solid $border-soft;
    background: $surface;
  }

  &__title {
    font-size: 15px;
    font-weight: 700;
    color: $text;
    margin: 0;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &__close {
    display: none;
  }

  &__scroll {
    flex: 1;
    overflow-y: auto;
    padding: 16px 18px 18px;
    display: flex;
    flex-direction: column;
    gap: 18px;

    scrollbar-width: thin;
    scrollbar-color: #d1d5db transparent;

    &::-webkit-scrollbar {
      width: 6px;
    }
    &::-webkit-scrollbar-thumb {
      background: #d1d5db;
      border-radius: 3px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
  }

  @media (max-width: 1023px) {
    position: fixed;
    inset: 64px 0 0 0;
    width: 100%;
    max-height: none;
    height: calc(100vh - 64px);
    border-radius: 0;
    border: none;
    z-index: 25;
    top: 64px;

    &__close {
      display: inline-flex;
    }
  }
}

// ── Filter groups ───────────────────────────────────────────────
.pa-filter-group {
  display: flex;
  flex-direction: column;
  gap: 12px;

  &__title {
    font-size: 11px;
    font-weight: 700;
    color: $muted;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin: 0;
    line-height: 1.2;
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  // ── Shared <Filters> component overrides ─────────────────────
  // The shared Filters component is built with tailwind utility
  // classes that overflow inside the narrow (280px) sidebar — the
  // groups row jams a 2/5 search + 3/5 action buttons together and
  // the magnifying-glass icon overlaps the input text. The
  // following deep selectors normalize that layout to the platform
  // theme without forking the shared component.
  ::v-deep {
    // Groups row — stack the search input above the action buttons
    // so they stop overlapping inside the narrow sidebar.
    .w-full.space-y-2 > .flex.justify-between {
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: 16px;
    }

    .relative.w-2\/5 {
      width: 100% !important;
    }

    .flex.space-x-2.w-3\/5 {
      width: 100% !important;
      justify-content: flex-end;
      gap: 8px;
      margin-left: 0 !important;

      > * {
        margin-left: 0 !important;
      }
    }

    // The groups search input: properly padded so the icon doesn't
    // sit on top of the placeholder text.
    .relative input[type='text'] {
      padding-right: 40px;
      border-radius: 9999px;
    }
    .relative .fa-search {
      position: absolute;
      right: 14px;
      top: 50%;
      transform: translateY(-50%);
      margin: 0;
      color: #9ca3af;
      font-size: 12px;
    }

    // Reset (refresh) action — outlined chip.
    .button.button-secondary {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      height: 30px;
      padding: 0 10px;
      border-radius: 9999px;
      background: transparent;
      color: $primary;
      border: 1px solid $primary;
      font-size: 11px;
      font-weight: 600;
      font-family: 'Poppins', sans-serif;
      margin: 0 !important;
      cursor: pointer;
      transition: background 0.15s, color 0.15s, border-color 0.15s;

      &:hover {
        background: rgba(4, 120, 115, 0.08);
        color: $primary-dark;
        border-color: $primary-dark;
      }

      i {
        font-size: 11px;
      }
    }

    // Save action — primary pill.
    .button.button-primary {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      height: 30px;
      padding: 0 14px;
      border-radius: 9999px;
      background: $primary;
      color: #ffffff;
      border: 1px solid $primary;
      font-size: 11px;
      font-weight: 600;
      font-family: 'Poppins', sans-serif;
      margin: 0 !important;
      cursor: pointer;
      transition: background 0.15s, border-color 0.15s;

      &:hover {
        background: $primary-dark;
        border-color: $primary-dark;
      }
    }

    // Group list — themed checkbox + spacing.
    .flex.flex-col.w-full.space-y-2.px-4 {
      padding: 4px 0 0;
      gap: 4px;

      > div {
        padding: 8px 10px;
        border-radius: 8px;
        font-size: 13px;
        color: $text;
        transition: background 0.15s;

        &:hover {
          background: $surface-alt;
        }
      }

      input[type='checkbox'] {
        appearance: none;
        -webkit-appearance: none;
        width: 16px;
        height: 16px;
        border: 1.5px solid #d1d5db;
        border-radius: 4px;
        background: #ffffff;
        cursor: pointer;
        position: relative;
        flex-shrink: 0;
        vertical-align: middle;
        margin-right: 4px;
        transition: border-color 0.15s, background 0.15s;

        &:checked {
          background: $primary;
          border-color: $primary;

          &::after {
            content: '';
            position: absolute;
            left: 4px;
            top: 1px;
            width: 5px;
            height: 9px;
            border: solid #ffffff;
            border-width: 0 2px 2px 0;
            transform: rotate(45deg);
          }
        }
      }
    }
  }
}

// ── Date select (inline, full control) ─────────────────────────
.pa-date-select {
  position: relative;
  width: 100%;

  &__field {
    box-sizing: border-box;
    display: block;
    width: 100%;
    height: 44px;
    padding: 0 40px 0 16px;
    border-radius: 10px;
    border: 1px solid $border;
    background: $surface;
    color: $text;
    font-size: 14px;
    font-weight: 500;
    line-height: 1;
    font-family: 'Poppins', sans-serif;
    outline: none;
    cursor: pointer;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    background-image: none;
    box-shadow: none;
    transition: border-color 0.15s, box-shadow 0.15s;

    &::-moz-focus-inner {
      border: 0;
    }

    &:hover {
      border-color: #cbd5e1;
    }

    &:focus {
      border-color: $primary;
      box-shadow: 0 0 0 3px rgba(4, 120, 115, 0.15);
    }
  }

  &__chevron {
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: $muted;
    font-size: 12px;
    pointer-events: none;
  }
}

// ── Date range card ────────────────────────────────────────────
.pa-date-range {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border: 1px solid $border;
  border-radius: 12px;
  background: $surface-alt;

  &__label {
    font-size: 11px;
    font-weight: 700;
    color: $muted;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  &__inputs {
    display: flex;
    gap: 8px;

    > * {
      flex: 1;
      min-width: 0;
    }
  }

  &__input {
    box-sizing: border-box;
    width: 100%;
    height: 38px;
    padding: 0 12px;
    border: 1px solid $border;
    border-radius: 9px;
    background: $surface;
    font-size: 13px;
    font-family: 'Poppins', sans-serif;
    color: $text;
    outline: none;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    box-shadow: none;
    transition: border-color 0.15s, box-shadow 0.15s;

    &:hover:not(:disabled) {
      border-color: #cbd5e1;
    }

    &:focus {
      border-color: $primary;
      box-shadow: 0 0 0 3px rgba(4, 120, 115, 0.12);
    }

    &:disabled {
      background: #f9fafb;
      color: #9ca3af;
      cursor: not-allowed;
    }

    &::-webkit-calendar-picker-indicator {
      cursor: pointer;
      opacity: 0.55;
      filter: invert(40%);
      transition: opacity 0.15s;
    }

    &::-webkit-calendar-picker-indicator:hover {
      opacity: 1;
    }
  }
}

// ── Checkbox row ────────────────────────────────────────────────
.pa-checkbox-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s;
  font-size: 13px;
  color: $text;

  &:hover {
    background: $surface-alt;
  }

  &__label {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    flex: 1;
    min-width: 0;
  }

  &__input {
    appearance: none;
    -webkit-appearance: none;
    width: 16px;
    height: 16px;
    border: 1.5px solid #d1d5db;
    border-radius: 4px;
    background: #ffffff;
    cursor: pointer;
    position: relative;
    flex-shrink: 0;
    transition: border-color 0.15s, background 0.15s;

    &:checked {
      background: $primary;
      border-color: $primary;

      &::after {
        content: '';
        position: absolute;
        left: 4px;
        top: 1px;
        width: 5px;
        height: 9px;
        border: solid #ffffff;
        border-width: 0 2px 2px 0;
        transform: rotate(45deg);
      }
    }

    &:focus-visible {
      outline: 2px solid rgba(4, 120, 115, 0.35);
      outline-offset: 2px;
    }
  }

  &__count {
    font-size: 12px;
    font-weight: 600;
    color: $muted;
    background: $surface-alt;
    padding: 2px 8px;
    border-radius: 9999px;
    min-width: 24px;
    text-align: center;
  }
}

// ── Main area ──────────────────────────────────────────────────
.pa-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

// Toolbar (search + export)
.pa-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;

  &__search {
    position: relative;
    flex: 1;
    min-width: 220px;
    max-width: 520px;
  }

  &__search-icon {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    color: #9ca3af;
    font-size: 13px;
  }

  &__search-input {
    width: 100%;
    height: 44px;
    padding: 0 16px 0 42px;
    border: 1px solid $border;
    border-radius: 9999px;
    background: $surface;
    font-size: 14px;
    color: $text;
    font-family: inherit;
    outline: none;
    transition: border-color 0.15s, box-shadow 0.15s;

    &::placeholder {
      color: #9ca3af;
    }
    &:focus {
      border-color: $primary;
      box-shadow: 0 0 0 3px rgba(4, 120, 115, 0.12);
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;
  }
}

// List header
.pa-list-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;

  &__title {
    font-size: 20px;
    font-weight: 700;
    color: $text;
    margin: 0;
    line-height: 1.2;
  }

  &__sub {
    font-size: 13px;
    color: $primary;
    font-weight: 500;
    margin: 4px 0 0;
  }
}

// ── Buttons ────────────────────────────────────────────────────
.pa-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 40px;
  padding: 0 18px;
  border-radius: 9999px;
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
  transition: background 0.15s, color 0.15s, border-color 0.15s, transform 0.15s;
  white-space: nowrap;

  i {
    font-size: 13px;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &--primary {
    background: $primary;
    color: #ffffff;

    &:hover:not(:disabled) {
      background: $primary-dark;
      transform: translateY(-1px);
    }
  }

  &--outline {
    background: transparent;
    color: $navy;
    border-color: $navy;

    &:hover:not(:disabled) {
      background: rgba(0, 24, 56, 0.08);
      color: $navy;
      border-color: $navy;
      transform: translateY(-1px);
    }
  }

  // Subtle pill — light grey outline + muted text. Used for
  // utility actions (open filters) that shouldn't compete with the
  // primary export button for attention.
  &--soft {
    height: 34px;
    padding: 0 14px;
    background: transparent;
    color: #374151;
    border-color: #cbd5e1;
    font-size: 12px;

    i {
      font-size: 11px;
      color: #374151;
    }

    &:hover:not(:disabled) {
      background: $surface-alt;
      color: $text;
      border-color: #94a3b8;
    }
  }

  &--ghost {
    background: transparent;
    color: $muted;
    border-color: transparent;
    height: 34px;
    padding: 0 12px;
    font-size: 12px;

    &:hover:not(:disabled) {
      color: $primary;
      background: $surface-alt;
    }
  }

  &__label:empty {
    display: none;
  }
}

// ── Loading ─────────────────────────────────────────────────────
.pa-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  color: $primary;

  i {
    font-size: 28px;
  }
}

// ── Table card ─────────────────────────────────────────────────
.pa-table-card {
  background: $surface;
  border: 1px solid $border;
  border-radius: 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.pa-table-scroll {
  width: 100%;
  overflow-x: auto;
  scrollbar-width: thin;
  scrollbar-color: #d1d5db transparent;

  &::-webkit-scrollbar {
    height: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
}

.pa-table {
  width: 100%;
  border-collapse: collapse;
  font-family: inherit;
  font-size: 13px;

  &__head {
    background: $navy;
    position: sticky;
    top: 0;
    z-index: 1;
  }

  &__th {
    text-align: left;
    padding: 14px 18px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: rgba(255, 255, 255, 0.92);
    white-space: nowrap;
  }

  &__tr {
    border-bottom: 1px solid $border-soft;
    transition: background 0.12s;

    &:hover {
      background: $surface-alt;
    }
    &:last-child {
      border-bottom: none;
    }
  }

  &__td {
    padding: 14px 18px;
    font-size: 13px;
    color: $text;
    vertical-align: middle;
  }
}

// Status pill (Scanned-In column)
.pa-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.4;

  i {
    font-size: 11px;
  }

  &--scanned {
    background: rgba(4, 120, 115, 0.1);
    color: $primary;
  }

  &--pending {
    background: #fef3c7;
    color: #b45309;
  }
}

// ── Empty state ────────────────────────────────────────────────
.pa-empty {
  text-align: center;
  padding: 60px 24px;

  &__icon {
    font-size: 36px;
    color: #d1d5db;
    margin-bottom: 12px;
  }

  &__text {
    margin: 0;
    color: $muted;
    font-size: 14px;
  }
}
</style>
